import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Common/Header';
import ProductModal from '../components/Products/ProductModal';
import ScreenshotCapture from '../components/Products/ScreenshotCapture';
// 需求 R152（PM 2026-09-01）：產品縮圖 hover 下載圖片小圖示。
// 下載行為與產品詳情頁共用 utils/productImageDownloader.js，見該元件檔頭。
import ProductImageDownloadButton from '../components/Products/ProductImageDownloadButton';
// 需求 R117（PM 2026-08-18 拍板 Q8=A；R3 PM 追加「四頁排列對齊客戶管理」）：
// 分頁列（含跳頁框）改用共用元件，本頁原本自寫的 renderPaginationButtons 一併移除。
import Pagination from '../components/Common/Pagination';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
// 需求 R129（PM 2026-08-21 拍板 Q8：批量修改一併修，解決有必填未填的錯誤）：
// 批量修改也不准把必填欄位改成空值，改前先驗證。volumeCbm 不在批量欄位內，僅檢查現有欄位即可。
import {
  PRODUCT_REQUIRED_FIELDS,
  isRequiredFieldFilled,
} from '../utils/productRequiredFields';
// 需求 R123 R2（PM 2026-08-25 追加留言逐字：「新增的任務為：將本案源任務中的
// 新增『建立時間區間』功能也套用在產品管理列表頁，位置亦同。」）：驗證規則與報價單建立／編輯
// 兩頁共用同一支 utils/dateRange.js（錯誤訊息、2 個月上限、只填一格也可搜尋皆為單一來源），
// 本頁不得再抄一份。
import { validateProductDateRange } from '../utils/dateRange';
// 需求 R106 R6：系列／類別的多選下拉改用與型錄管理相同的呈現（樣式單一來源）。
import '../styles/multiSelectDropdown.css';
// 需求 R123 R2：.date-range-inputs / .date-input / .date-range-sep / .date-range-error 的
// 子元素樣式與型錄／報價單共用（AC4「不可複製貼上」）；父容器 .new-product-date-range 的箱型
// 仍由 CatalogPage.css 提供（CRA 全域樣式表，App.js 靜態 import 所有頁面故必定在 bundle 內），
// 本頁只在 ProductsPage.css 覆寫成「同列並排」所需的無 chrome 版本。
import '../styles/dateRangeFilter.css';
import './ProductsPage.css';

const API = process.env.REACT_APP_API_URL || '/api';

// 需求 R43：列表⇄詳情往返時還原搜尋／篩選與分頁所用的 sessionStorage key。
// RETURN_ARMED_KEY 僅在「自列表點詳情/編輯離開」時被設為 '1'，列表 mount 取用後立即清除；
// 因此只有「列表→詳情→返回列表」這一次往返會還原，從選單重新進入或重新整理（未 arm）皆為乾淨清單。
const RETURN_ARMED_KEY = 'productsListReturnArmed';
const LIST_SNAPSHOT_KEY = 'productsListSnapshot';

// Helper: volume method label
const volumeMethodLabel = (method) => {
  switch (method) {
    case 'auto': return '自動';
    case 'supplier': return '廠商';
    case 'estimate': return '預估';
    default: return '';
  }
};

// Helper: volume method CSS class
// 需求 R6：'estimate' 僅徽章本身（.cbm-estimate）顯示黃色底色作為視覺標示，
// 材積數值「不」加底色（PM 2026-05-08 二次確認的最終版本）。
const volumeMethodClass = (method) => {
  switch (method) {
    case 'auto': return 'cbm-auto';
    case 'supplier': return 'cbm-vendor';
    case 'estimate': return 'cbm-estimate';
    default: return '';
  }
};

// Helper: format price
// 需求 R77（PM 2026-07-20 留言拍板「A」）：標準成本欄一律「最多到小數點第一位、
// 無條件進位」，固定顯示一位小數（整數補 .0，例：350 → $350.0；813.7 → $813.7；813.75 → $813.8），
// 與 需求 R70 詳細頁 ProductDetailPage.js fmtMoney / 報價單 QuoteCreatePage 一致。
// 先 toFixed(6) 清浮點雜訊再 ×10 無條件進位、÷10 還原，最後保 $ 前綴＋千分位分隔，空值 fallback「—」。
const fmtPrice = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const scaled = Number((n * 10).toFixed(6));
  const ceiled = Math.ceil(scaled) / 10;
  return `$${ceiled.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
};

// Catalog status → badge mapping
// 需求 R49 Phase 2（PM 2026-06-17 留言）：
// 「色票參考型錄」→ 改指向新建的 .status-badge-mkt-* 局部 class（同 ProductsPage.css），
// 與型錄頁 .mkt-status-active/-pending/-inactive 同色，但保留 .status-badge 形狀規範。
// 不修改全域 .status-badge-warning/info/secondary，避免波及 QuotesPage 等其他頁面。
const catalogStatusBadge = {
  in_catalog: { label: '上目錄', cls: 'status-badge-mkt-active' }, // 綠（原 warning 橘黃）
  not_in_catalog: { label: '未上目錄', cls: 'status-badge-secondary' }, // PM 未指定，維持灰
};
const websiteStatusBadge = {
  pending_website: { label: '要上官網', cls: 'status-badge-mkt-pending' }, // 橘（原 info 藍），對齊型錄
  on_website: { label: '已上官網', cls: 'status-badge-mkt-active' }, // 綠（原 success 綠），對齊型錄綠色 hex
  // 需求 R128（PM 2026-08-21）：標籤「不上行銷」改回「不上官網」（值不動）；新增「官網需下架」。
  not_on_website: { label: '不上官網', cls: 'status-badge-mkt-inactive' }, // 藍（原 secondary 灰），呼應 需求 R48
  // 需求 R128 R2（PM 2026-08-21 第二輪留言：「官網需下架，顏色配置與『要上官網』相同。」）：
  // 徽章 class 維持獨立，但 .status-badge-mkt-delist 的色值已改為與 pending（要上官網）同橘。
  delist_website: { label: '官網需下架', cls: 'status-badge-mkt-delist' }, // 橘（同 pending_website）
};

// 需求 R106 Q2=A：把任何來源（新版陣列／舊版 sessionStorage 快照字串／null）正規化成字串陣列。
// 舊快照相容是必要的：需求 R43 會把離開列表前的篩選狀態存進 sessionStorage，使用者在改版前
// 存下的 seriesFilter 是字串（如 '醬料'），改版後那份快照仍可能被讀回來。
const toFilterList = (raw) => {
  if (Array.isArray(raw)) return raw.filter((v) => v != null && v !== '');
  if (typeof raw === 'string' && raw !== '') return [raw];
  return [];
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);

  // 需求 R43：若本次 mount 是「自詳情頁返回列表」（已被 arm），讀出離開前存下的搜尋／篩選快照，
  // 作為下列各 state 的初始值（一次讀取、純讀無副作用，StrictMode 重複呼叫也安全）。
  const [bootSnapshot] = useState(() => {
    try {
      if (sessionStorage.getItem(RETURN_ARMED_KEY) !== '1') return null;
      const raw = sessionStorage.getItem(LIST_SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [page, setPage] = useState(() => bootSnapshot?.page ?? 1);
  const [limit, setLimit] = useState(() => bootSnapshot?.limit ?? 50);

  // Pending filter state (staged, not applied until confirm button clicked)
  const [pendingKeyword, setPendingKeyword] = useState(() => bootSnapshot?.pendingKeyword ?? '');
  // 需求 R106 Q2=A（PM 2026-08-04「四頁統一改多選」）：系列／類別由單選 <select>
  // 改為可複選，state 型別 string → string[]。toFilterList 同時負責相容 需求 R43 存在
  // sessionStorage 的舊快照（改版前存的是字串，直接餵給 .map 會爆），空字串一律視為「未篩選」。
  const [pendingSeries, setPendingSeries] = useState(() => toFilterList(bootSnapshot?.pendingSeries));
  const [pendingCategory, setPendingCategory] = useState(() => toFilterList(bootSnapshot?.pendingCategory));
  const [pendingTags, setPendingTags] = useState(() => toFilterList(bootSnapshot?.pendingTags));
  // 需求 R123 R2：「建立時間區間」比照本頁其他篩選欄採 pending → applied 兩段式（按「確定篩選」
  // 才生效），與報價單兩頁的「即時 state + 按鈕觸發」等價（都只在按鈕當下才打 API），
  // 差別僅在本頁本來就有 pending 這層，日期欄若不跟著做就會出現「改了日期不按確定也生效」的不一致。
  // PM 沿用源任務拍板：Q1=B 預設空白、Q2=B 只填一格也可搜尋、Q3=B 清除即清空。
  const [pendingCreatedFrom, setPendingCreatedFrom] = useState(() => bootSnapshot?.pendingCreatedFrom ?? '');
  const [pendingCreatedTo, setPendingCreatedTo] = useState(() => bootSnapshot?.pendingCreatedTo ?? '');
  const [dateRangeError, setDateRangeError] = useState('');

  // Applied filter state (drives API calls)
  const [keyword, setKeyword] = useState(() => bootSnapshot?.keyword ?? '');
  const [seriesFilter, setSeriesFilter] = useState(() => toFilterList(bootSnapshot?.seriesFilter));
  const [categoryFilter, setCategoryFilter] = useState(() => toFilterList(bootSnapshot?.categoryFilter));
  const [tagsFilter, setTagsFilter] = useState(() => toFilterList(bootSnapshot?.tagsFilter));
  // 需求 R123 R2：applied 版（真正進 query 的值），由 handleApplyFilters 自 pending 複製。
  const [createdFromFilter, setCreatedFromFilter] = useState(() => bootSnapshot?.createdFromFilter ?? '');
  const [createdToFilter, setCreatedToFilter] = useState(() => bootSnapshot?.createdToFilter ?? '');
  const [statusFilter] = useState('');

  // options for filters
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  // Tags dropdown
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef(null);

  // 需求 R106 R6：系列／類別多選下拉各自的開／關 state + click-outside 用的 ref。
  // 呈現改用共用的 .multi-select-*（styles/multiSelectDropdown.css，四頁同一份來源），
  // R3 時原本沿用同頁「標籤」用的 .tag-dropdown 已淘汰，樣式與開關結構皆與型錄管理對齊。
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  // 需求 R62（PM 2026-07-10 拍板方案 A）：跨分頁全選按鈕載入狀態，
  // 取回全部符合商品期間顯示「處理中...」並防重複點擊。
  const [selectAllAcrossLoading, setSelectAllAcrossLoading] = useState(false);

  // batch edit
  const [batchEditMode, setBatchEditMode] = useState(false);
  const [batchEditData, setBatchEditData] = useState({});
  const [batchSaving, setBatchSaving] = useState(false);
  const imageInputRef = useRef(null);
  const imageUploadCtxRef = useRef(null); // { productId } target for upload

  // 需求 R39：匯入產品（Excel）
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // 需求 R108（PM 2026-08-05 Q4：「匯出全部商品可能是常態需求，但不需要有筆數提示」）：
  // 全量 8,166 筆匯出後端需數秒才回傳 xlsx，加匯出中旗標防連點並讓按鈕顯示「匯出中…」，
  // 但依 Q4 不顯示筆數（不做「正在匯出 N 筆…」提示），也不加全頁 loading 遮罩。
  const [exportingCostTable, setExportingCostTable] = useState(false);

  // delisted filter toggle (pending + applied)
  const [pendingDelisted, setPendingDelisted] = useState(() => !!bootSnapshot?.pendingDelisted);
  const [delistedFilterActive, setDelistedFilterActive] = useState(() => !!bootSnapshot?.delistedFilterActive);

  // append panel (inline)
  const [appendPanelOpen, setAppendPanelOpen] = useState(false);
  const [appendKeyword, setAppendKeyword] = useState('');
  const [appendResults, setAppendResults] = useState([]);
  const [appendSearching, setAppendSearching] = useState(false);
  const [appendSelected, setAppendSelected] = useState([]);
  const appendSearchRef = useRef(null);
  // 需求 R102 第七輪（PM 2026-08-03 留言：「傾向在這裡的做法是確定有追加並僅需要在
  // 『已選取Ｎ項產品』有數量調整就好，不用固定顯示在每一頁，同時將『追加』兩字取消」）——
  // 這則留言同時拍板了兩個先前掛著的問題，並推翻了先前的 Q4=B：
  // Q7 → 追加的商品「不固定顯示在每一頁」。原本（b1b87ce~8c64f93）用 appendedProductsRef 把追加過的
  // 商品記在 ref、每次 fetchProducts 完再補回清單尾端；本輪整組移除，追加的列只存在於
  // 「按下確認追加當下的那一頁清單」，換頁／改篩選重新查詢後就回到後端回傳的純查詢結果。
  // 「追加」標記 → 一併移除（原本追加的列會多一顆黃色「追加」badge）。標記存在的唯一理由是解釋
  // 「為什麼換頁後這幾列還跟著我」，固定顯示行為既然拿掉了，標記也就沒有意義。
  // 保留不動的是 PM 本次唯一要求要有的行為：追加後「已選取 N 項產品」的數量要跟著加
  //（見 handleAppendConfirm 的 setSelectedIds）。selectedIds 是純 id 陣列，不受清單有沒有顯示影響，
  // 所以換頁後那幾筆仍算在計數裡，只是列本身不再被硬補到畫面上（由 offPageSelectedCount 交代）。

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token || !user._id) { navigate('/login'); return; }
    setCurrentUser(user);
  }, [navigate]);

  // 需求 R43：列表 mount 後立刻清除 arm 旗標——確保只有「離開列表→返回列表」這一次往返會還原；
  // 之後在列表上重新整理、或從上方選單重新進入產品管理（皆未再 arm）都會得到乾淨清單（PM D1）。
  useEffect(() => {
    try { sessionStorage.removeItem(RETURN_ARMED_KEY); } catch { /* sessionStorage 不可用時略過 */ }
  }, []);

  // Close tags dropdown on outside click（需求 R106：系列／類別多選下拉一併納入同一個監聽）
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(e.target)) {
        setTagsDropdownOpen(false);
      }
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(e.target)) {
        setSeriesDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close append search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (appendSearchRef.current && !appendSearchRef.current.contains(e.target)) {
        setAppendResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOptions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const [sRes, cRes, tRes] = await Promise.all([
        fetch(`${API}/products/options?type=series`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/products/options?type=category`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/products/options?type=tag`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (sRes.ok) setSeriesOptions(await sRes.json());
      if (cRes.ok) setCategoryOptions(await cRes.json());
      if (tRes.ok) setTagOptions(await tRes.json());
    } catch (err) {
      console.error('載入選項失敗', err);
    }
  }, []);

  // 需求 R62：抽出目前列表的篩選條件（keyword/series/category/tags/status/下架），
  // 供「列表查詢 fetchProducts」與「跨分頁全選 handleSelectAllAcrossPages」共用。
  // 需求 R106 R4：原本這裡與 fetchProducts 內是兩份手抄的相同組裝碼——後端已在 R3 靠
  // buildProductListFilter 讓 getAll/exportProducts 結構性同步（AC6），前端卻仍是「改一處
  // 要記得改另一處」。AC9 要求「跨頁全選與匯出結果和列表相同」，故此處收斂成單一 builder，
  // 由 fetchProducts 直接沿用（只另加 page/limit），兩條路徑的 query 語意從此不可能分歧。
  // 匯出（handleExportCostTable）自 需求 R69 D1 起一律帶 selectedIds、不再走篩選 fallback，
  // 其「與列表一致」是透過「先按全選所有商品（用本 builder）再匯出」達成（需求 R69 D2）。
  // 需求 R120 已由重新立案的 需求 R131 取代（PM 2026-08-21 逐字：「此案我會
  // 依據個頁面的篩選條件設定重新立案，本任務結案」）。
  //
  // 需求 R131（PM 2026-08-21 拍板）：
  // 「產品管理：進階篩選中的所有篩選欄位皆用 AND，後續新增的日期區間也是，複選部分同樣適用 AND。」
  // Q3=A：「複選也 AND」指的是各欄位**之間** AND；同一欄多選仍是任一符合（系列／類別在 DB 是
  // 單一字串欄位，同欄多值若當成 AND 必定 0 筆）。
  // Q6=A：不在 UI 加「目前為同時符合」的提示文字。
  // 實作方式：後端 buildProductListFilter 以 orFields（要併進同一包 OR 的欄位清單）參數化，
  // 本頁固定送出**空清單**＝系列／類別／標籤／關鍵字彼此全部 AND。四頁共用同一支 builder，
  // 要分岔只能靠這個參數，不可改後端預設值（會一次改掉四頁）。
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    // 需求 R131：明確送出空 orFields＝「沒有任何欄位併進 OR」。後端未帶此參數時的預設也是
    // 全 AND（見 productController.js 檔頭），這裡仍明寫出來，讓「本頁是全 AND」在 code 與
    // 網址上都看得見，也讓 fixture 能靜態守門（AC13）。
    params.set('orFields', '');
    if (keyword) params.set('keyword', keyword);
    // 需求 R106：三組皆為多選，一律以逗號串送出（後端 buildProductListFilter 會 split 後
    // 組成 $in＝同欄任一符合）；需求 R131 起三組彼此為 AND。
    if (seriesFilter.length > 0) params.set('series', seriesFilter.join(','));
    if (categoryFilter.length > 0) params.set('category', categoryFilter.join(','));
    if (tagsFilter.length > 0) params.set('tags', tagsFilter.join(','));
    // 需求 R123 R2 ＋ 需求 R131（PM 2026-08-21 逐字：「產品管理：進階篩選中的
    // 所有篩選欄位皆用 AND，後續新增的日期區間也是」）：日期區間刻意**不**加入 orFields，
    // 靠上面那行送出的空 orFields 讓後端把 createdAt 放在頂層 AND（productController
    // 的 orKeys.has('created') 為 false 那條路徑）——與報價單兩頁（created 併進 OR）相反，
    // 這是 PM 逐字要求的頁面差異，改動前請先回頭讀 需求 R131 拍板。
    // Q2=B：只填一格也可搜尋，空的那格不送（後端 $gte / $lte 各自獨立）。
    if (createdFromFilter) params.set('createdFrom', createdFromFilter);
    if (createdToFilter) params.set('createdTo', createdToFilter);
    if (delistedFilterActive) {
      params.set('status', 'delisted');
    } else if (statusFilter) {
      params.set('status', statusFilter);
    }
    return params;
  }, [keyword, seriesFilter, categoryFilter, tagsFilter, createdFromFilter, createdToFilter, statusFilter, delistedFilterActive]);

  const fetchProducts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = buildFilterParams();
    params.set('page', page);
    params.set('limit', limit);

    try {
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // 需求 R102 第七輪（PM「不用固定顯示在每一頁」）：清單與總筆數一律以後端查詢結果為準，
        // 不再把本頁 session 追加過的商品補回清單尾端（連帶也不會再有 total 被多算的問題）。
        setProducts(data.products || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('載入產品失敗', err);
    }
  }, [page, limit, buildFilterParams]);

  useEffect(() => {
    if (currentUser) {
      fetchOptions();
      fetchProducts();
    }
  }, [currentUser, fetchOptions, fetchProducts]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // 需求 R129 R3（PM 2026-08-21 追問 Q6：「若只是新增商品，與欄位必填的關聯性為何？」）：
  // openAdd 是全專案唯一會 setModalOpen(true) 的函式，但它**沒有任何呼叫端**——畫面上的
  // 「新增產品」按鈕（本檔 L1311）走的是 navigate('/products/new') → ProductDetailPage。
  // 也就是說 <ProductModal open={modalOpen}> 永遠是 open=false（ProductModal.js `if (!open)
  // return null`），彈窗目前使用者點不到，「新增商品」的必填驗證在 R1（2bfcb56）就已隨
  // ProductDetailPage 生效。此處保留 openAdd 不刪（是否移除彈窗屬另一個決策），但已於
  // tests/productModalReachability.fixture.js 鎖住「零呼叫端」這個事實：日後若有人
  // 把它接回去，該 fixture 會紅，提醒必須同步檢查彈窗端的必填驗證。
  // eslint-disable-next-line no-unused-vars
  const openAdd = () => { setSelectedProduct(null); setModalMode('add'); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedProduct(null); };

  const handleSave = async (formData) => {
    const token = localStorage.getItem('token');
    const isEdit = modalMode === 'edit' && selectedProduct;
    const url = isEdit ? `${API}/products/${selectedProduct._id}` : `${API}/products`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        closeModal();
        fetchProducts();
        fetchOptions();
      } else {
        const err = await res.json();
        alert(err.message || '儲存失敗');
      }
    } catch (err) {
      console.error('儲存失敗', err);
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        closeModal();
        fetchProducts();
      }
    } catch (err) {
      console.error('刪除失敗', err);
    }
  };

  const handleBatchDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`確定要刪除 ${selectedIds.length} 筆產品？`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/products/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (res.ok) {
        setSelectedIds([]);
        fetchProducts();
      }
    } catch (err) {
      console.error('批量刪除失敗', err);
    }
  };

  // Apply all pending filters
  const handleApplyFilters = () => {
    // 需求 R123 R2：跨度驗證只在按下「確定篩選」當下做（與報價單 Q4=A 同一時機）。
    // 驗證失敗就顯示 .date-range-error 並整個 return——不套用任何 pending 值、不改 page、
    // 不觸發 fetchProducts，避免「日期不合法卻套用了其他條件」的半套狀態。
    const err = validateProductDateRange(pendingCreatedFrom, pendingCreatedTo);
    if (err) {
      setDateRangeError(err);
      return;
    }
    setDateRangeError('');
    setCreatedFromFilter(pendingCreatedFrom);
    setCreatedToFilter(pendingCreatedTo);
    setKeyword(pendingKeyword);
    // 需求 R106 Q2=A：三組都是陣列，一律複製一份避免與 pending 共用參考。
    setSeriesFilter([...pendingSeries]);
    setCategoryFilter([...pendingCategory]);
    setTagsFilter([...pendingTags]);
    setDelistedFilterActive(pendingDelisted);
    setPage(1);
  };

  const handleClearFilters = () => {
    setPendingKeyword('');
    setPendingSeries([]);
    setPendingCategory([]);
    setPendingTags([]);
    setPendingDelisted(false);
    // 需求 R123 R2（Q3=B 清除＝清空，不還原成任何預設區間）：pending／applied／錯誤訊息三者都清。
    setPendingCreatedFrom('');
    setPendingCreatedTo('');
    setDateRangeError('');
    setKeyword('');
    setSeriesFilter([]);
    setCategoryFilter([]);
    setTagsFilter([]);
    setCreatedFromFilter('');
    setCreatedToFilter('');
    setDelistedFilterActive(false);
    setPage(1);
  };

  // 需求 R43：點「詳情／編輯」離開列表前，存下目前搜尋／篩選與分頁快照並 arm；
  // 返回列表（返回鍵、瀏覽器上一頁、儲存並返回、取消）時 mount 會讀出此快照還原。
  // pending 與 applied 兩組都存，讓搜尋框／下拉的視覺狀態也一併還原（AC2）。
  const persistListStateForReturn = useCallback(() => {
    try {
      const snapshot = {
        keyword, seriesFilter, categoryFilter, tagsFilter, delistedFilterActive,
        pendingKeyword, pendingSeries, pendingCategory, pendingTags, pendingDelisted,
        // 需求 R123 R2：日期區間同樣要跟著列表⇄詳情往返還原，否則返回列表時清單是照日期
        // 篩過的、但欄位卻空白（使用者會以為篩選失效）。pending 與 applied 兩組都存（AC2 同理）。
        pendingCreatedFrom, pendingCreatedTo, createdFromFilter, createdToFilter,
        page, limit,
      };
      sessionStorage.setItem(LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
      sessionStorage.setItem(RETURN_ARMED_KEY, '1');
    } catch {
      /* sessionStorage 不可用時略過，不影響導頁 */
    }
  }, [keyword, seriesFilter, categoryFilter, tagsFilter, delistedFilterActive, pendingKeyword, pendingSeries, pendingCategory, pendingTags, pendingDelisted, pendingCreatedFrom, pendingCreatedTo, createdFromFilter, createdToFilter, page, limit]);

  // Toggle pending tag (not applied until confirm)
  const togglePendingTag = (tagValue) => {
    setPendingTags((prev) =>
      prev.includes(tagValue) ? prev.filter((t) => t !== tagValue) : [...prev, tagValue]
    );
  };

  // 需求 R106 Q2=A：系列／類別的多選切換，語意與 togglePendingTag 相同（勾了再點＝取消）。
  const togglePendingSeries = (value) => {
    setPendingSeries((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };
  const togglePendingCategory = (value) => {
    setPendingCategory((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  // === Batch Edit ===
  const handleEnterBatchEdit = async () => {
    if (!selectedIds.length) {
      alert('請先勾選要修改的產品');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const detailPromises = selectedIds.map((id) =>
        fetch(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.ok ? r.json() : null)
      );
      const details = await Promise.all(detailPromises);
      const initialData = {};
      for (const p of details) {
        if (!p) continue;
        initialData[p._id] = {
          productImage: p.productImage || '',
          productName: p.productName || '',
          englishName: p.englishName || '',
          spec: p.spec || '',
          pcsPerBox: p.pcsPerBox ?? '',
          moq: p.factoryCost?.moq || '',
          shelfLife: p.shelfLife || '',
          netWeight: p.netWeight ?? '',
          grossWeight: p.grossWeight ?? '',
          barcode: p.barcode || '',
        };
      }
      setBatchEditData(initialData);
      setBatchEditMode(true);
    } catch (err) {
      console.error('載入產品詳情失敗', err);
      alert('載入產品詳情失敗');
    }
  };

  const handleBatchEditChange = (productId, field, value) => {
    setBatchEditData((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
  };

  const handleBatchEditSave = async () => {
    // 需求 R129 Q8：批量修改也不准把必填欄位改成空值。materialCbm 不在批量欄位內，
    // 因此只驗批量表格內出現的必填欄位（productName／spec／pcsPerBox／shelfLife／
    // netWeight／grossWeight），一筆有缺就用 alert 列產品編號＋缺欄名稱，
    // 不呼叫 API，使用者已改好的其他欄位完整保留（batchEditData 不清）。
    const batchEditableRequiredKeys = ['productName', 'spec', 'pcsPerBox', 'shelfLife', 'netWeight', 'grossWeight'];
    const missingReports = [];
    for (const [pid, fields] of Object.entries(batchEditData)) {
      const missingKeysThisProduct = batchEditableRequiredKeys.filter((k) => {
        if (!Object.prototype.hasOwnProperty.call(fields, k)) return false;
        return !isRequiredFieldFilled(k, fields[k]);
      });
      if (missingKeysThisProduct.length > 0) {
        const prod = products.find((p) => String(p._id) === String(pid));
        const label = prod ? (prod.systemCode || prod.productCode || prod.productName || pid) : pid;
        const labels = missingKeysThisProduct.map((k) => {
          const def = PRODUCT_REQUIRED_FIELDS.find((f) => f.key === k);
          return def ? def.label : k;
        });
        missingReports.push(`${label}：${labels.join('、')}`);
      }
    }
    if (missingReports.length > 0) {
      alert(`以下產品有必填欄位未填，請補齊後再儲存：\n${missingReports.join('\n')}`);
      return;
    }
    setBatchSaving(true);
    const token = localStorage.getItem('token');
    const updates = Object.entries(batchEditData).map(([id, fields]) => {
      const data = { _id: id };
      if (fields.pcsPerBox !== undefined) data.pcsPerBox = fields.pcsPerBox === '' ? null : Number(fields.pcsPerBox);
      if (fields.netWeight !== undefined) data.netWeight = fields.netWeight === '' ? null : Number(fields.netWeight);
      if (fields.grossWeight !== undefined) data.grossWeight = fields.grossWeight === '' ? null : Number(fields.grossWeight);
      if (fields.productImage !== undefined) data.productImage = fields.productImage;
      if (fields.productName !== undefined) data.productName = fields.productName;
      if (fields.englishName !== undefined) data.englishName = fields.englishName;
      if (fields.spec !== undefined) data.spec = fields.spec;
      if (fields.moq !== undefined) data.moq = fields.moq;
      if (fields.shelfLife !== undefined) data.shelfLife = fields.shelfLife;
      if (fields.barcode !== undefined) data.barcode = fields.barcode;
      return data;
    });

    try {
      const res = await fetch(`${API}/products/batch-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ updates }),
      });
      if (res.ok) {
        setBatchEditMode(false);
        setBatchEditData({});
        fetchProducts();
        alert('批量修改儲存成功');
      } else {
        const err = await res.json();
        alert(err.message || '批量修改失敗');
      }
    } catch (err) {
      console.error('批量修改失敗', err);
      alert('批量修改失敗');
    } finally {
      setBatchSaving(false);
    }
  };

  const handleBatchEditCancel = () => {
    setBatchEditMode(false);
    setBatchEditData({});
  };

  const triggerBatchImageUpload = (productId) => {
    if (!productId) return;
    imageUploadCtxRef.current = { productId };
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const handleBatchImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    const ctx = imageUploadCtxRef.current;
    const token = localStorage.getItem('token');
    if (!file || !ctx?.productId || !token) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API}/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || '圖片上傳失敗');
      }
      const { url } = await res.json();
      handleBatchEditChange(ctx.productId, 'productImage', url || '');
    } catch (err) {
      alert(err.message || '圖片上傳失敗');
    } finally {
      imageUploadCtxRef.current = null;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  // 需求 R62（PM 2026-07-13 留言第二點）：表頭「全選」勾選框嚴格以「目前分頁」為範圍。
  // 舊版以 selectedIds.length === products.length 判定是否已全選——這只是「數量相等」而非「本頁確實都被勾」。
  // 造成的問題：第 1 頁勾滿 50 筆後切到第 2 頁，selectedIds.length(50) 仍等於 products.length(50)，
  // checkbox 顯示已勾選，但本頁其實沒有任何一筆被選；此時想全選本頁而點一下，反而觸發「取消全選」把
  // 前一頁的 50 筆清光，使用者無法選取新分頁。改為以「本頁每一筆 _id 是否都在 selectedIds」判定，
  // 切換時只增減『本頁』的 id（勾＝聯集本頁、取消＝從選取移除本頁），完全不動其他分頁既有的勾選。
  const currentPageIds = products.map((p) => p._id);
  const isCurrentPageAllSelected =
    products.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));

  // 需求 R102 第二輪（PM 2026-08-03 留言「原本勾選的沒有顯示，只有數量顯示正確，是否有誤導的疑慮」）：
  // offPageSelectedCount＝已勾選但不在目前分頁上的筆數，用來在計數旁講明「還有 N 項在別的分頁」，
  // 使用者才不會把「畫面上只有 2 個勾、計數卻寫 52」誤讀成計數壞掉。
  // 第七輪：追加商品不再固定顯示於每一頁，換頁後它們也屬於「不在目前頁面的已勾選項」，由本計數交代。
  const currentPageIdSet = new Set(currentPageIds);
  const offPageSelectedCount = selectedIds.filter((id) => !currentPageIdSet.has(id)).length;

  const toggleSelectAll = () => {
    if (isCurrentPageAllSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    }
  };

  // 需求 R106 R4：buildFilterParams 已上移至 fetchProducts 之前（全檔單一份，見該處註解），
  // 下方跨分頁全選直接呼叫同一個 builder，不再另抄一份。

  // 需求 R62（PM 2026-07-10 拍板方案 A）：跨分頁全選——
  // 參考型錄管理 CatalogPage.handleSelectAllAcrossPages（需求 R24）。現況表頭「全選」勾選框
  // （toggleSelectAll）只勾目前分頁顯示的 ~20 筆；本函式以相同篩選條件一次取回「全部符合商品」
  // 的 _id 並全部勾選（含其他分頁）。已達全選（selectedIds ≥ total）時再點一次＝清空全部勾選。
  const handleSelectAllAcrossPages = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (total > 0 && selectedIds.length >= total) {
      setSelectedIds([]);
      return;
    }
    setSelectAllAcrossLoading(true);
    try {
      const params = buildFilterParams();
      params.set('page', 1);
      // 後端 getAll 接受 limit；以遠大於實務產品量的上限一次取回全部 id。
      params.set('limit', 100000);
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        alert('全選所有商品失敗，請稍後再試');
        return;
      }
      const data = await res.json();
      const all = data.products || [];
      // 需求 R102 第七輪：追加進來的商品不符合目前篩選條件時不會出現在這次查詢結果裡，但只要它們
      // 還顯示在目前清單上（＝剛按完「確認追加」、尚未換頁／改篩選），就得一併勾選，否則會出現
      // 「按了全選所有商品，畫面上卻還有沒打勾的列」。改讀目前清單（products）而不是先前的 session ref，
      // 因為固定顯示行為已移除，換頁後那些列本來就不在畫面上了。
      const allIds = new Set(all.map((p) => p._id));
      const onScreenExtraIds = products.map((p) => p._id).filter((id) => !allIds.has(id));
      setSelectedIds([...all.map((p) => p._id), ...onScreenExtraIds]);
    } catch (err) {
      console.error('全選所有商品失敗', err);
      alert('全選所有商品失敗，請稍後再試');
    } finally {
      setSelectAllAcrossLoading(false);
    }
  };

  const handleDelistedToggle = () => {
    // 點擊即套用：pendingDelisted 與 delistedFilterActive 必須保持同步
    const next = !pendingDelisted;
    setPendingDelisted(next);
    setDelistedFilterActive(next);
    setPage(1);
  };

  // 需求 R51（PM 2026-06-18 第二輪 C9 拍板）：
  // - 產品管理頁既有按鈕 reuse，不新增 UI
  // - 資料範圍 = 當前列表的篩選（與 fetchProducts params 對齊；不帶 page/limit 由後端不分頁）
  // - 檔名 products_YYYYMMDD.xlsx 由後端 Content-Disposition 提供，前端 fallback 用同公式
  const handleExportCostTable = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('未登入');
      return;
    }
    // 需求 R69（PM 2026-07-19 拍板 D1～D4）：改為「未勾選就擋下、不匯出」。
    // - D1：本案（勾選才能匯出）與 需求 R62（全選按鈕／當頁勾選功能）不衝突，撤回原
    // 「未勾選＝依當前篩選匯出全部」的 fallback。
    // - D2：使用者若「就是想匯出全部」，仍走 需求 R62 既有的「全選所有商品（N）」按鈕先全選
    // （handleSelectAllAcrossPages 會把所有符合條件的 _id 放進 selectedIds），再匯出即可，
    // 故不需保留一鍵匯出全部的入口。
    // - D3：提示用瀏覽器原生 alert，與「批量修改未勾選」（handleEnterBatchEdit）一致。
    // - D4：跨分頁全選後 selectedIds 已含所有符合條件的 _id，視為「有勾選」，正常匯出、不觸發提示。
    if (selectedIds.length === 0) {
      alert('請先勾選要匯出的產品');
      return;
    }
    // 需求 R108（PM 2026-08-05 拍板 Q1=A / Q4）：
    // - 原本是唯一一支把勾選的 _id 串在 GET query string 上的請求
    // （`?ids=` + 24 字元 ObjectId × N）。全選 8,166 筆時網址長達 204,149 字元，
    // 超過 Node http.maxHeaderSize 預設 16KB（request line 與所有 header 共用這 16KB），
    // 請求在 HTTP parser 就被 HPE_HEADER_OVERFLOW 擋下、回 431 空 body，
    // 連 exportProducts controller 都進不去。實測 630 筆 OK、631 筆即 431。
    // - 改為 POST + JSON body（後端 express.json limit '50mb'），比照本專案既有範式
    // POST /customers/export（CustomersPage.js handleExport），無筆數上限。
    // - Q4：PM「匯出全部商品可能是常態需求，但不需要有筆數提示」→ 加匯出中防連點／
    // 按鈕顯示「匯出中…」，但不顯示「正在匯出 8,166 筆…」這類筆數提示。
    // - Q2=B（PM 2026-08-05 第二輪「收斂成 POST 一條：程式更乾淨」）：後端已移除
    // GET /products/export.xlsx，此處是全專案唯一的匯出成本表呼叫端，必須是 POST。
    if (exportingCostTable) return;
    setExportingCostTable(true);
    try {
      const res = await fetch(`${API}/products/export.xlsx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // 一律以 selectedIds 為準、帶 ids 匯出勾選項（後端精確依 _id 匯出，含下架）。
        // 需求 R131 Q8/AC14：orFields 一併帶上，與本頁列表送的是同一個值（全 AND）。
        // 目前 ids 必定非空（上方已擋），後端會以 { _id: { $in: ids } } 覆蓋篩選條件，故這個
        // 參數在現行流程不生效；明寫是為了「列表／全選／匯出三條路徑送同一組參數」的結構保證——
        // 日後若恢復「未勾選就依當前篩選匯出全部」的路徑，不會因為漏帶而語義分岔。
        body: JSON.stringify({ ids: selectedIds, orFields: '' }),
      });
      if (!res.ok) {
        // 需求 R108（PM 拍板 Q3=B「維持單純『匯出失敗，請稍後再試』」）：不把 HTTP 狀態碼／
        // 後端 error 等技術細節丟給使用者，一律顯示同一句可讀訊息。
        alert('匯出失敗，請稍後再試');
        return;
      }
      const blob = await res.blob();
      // 後端 Content-Disposition 已提供 products_YYYYMMDD.xlsx；前端 fallback 用同公式
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const filename = (m && m[1]) || `products_${yyyy}${mm}${dd}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // 需求 R108 Q3=B：網路層錯誤同樣不外露 err.message（可能是英文的 TypeError: Failed to fetch）。
      console.error('匯出成本表失敗', err);
      alert('匯出失敗，請稍後再試');
    } finally {
      setExportingCostTable(false);
    }
  };

  // 需求 R39：匯入產品（依英文欄碼共通規範，與 server productImportParser 共用同一規範）
  const handleImportProducts = () => {
    if (importing) return;
    importInputRef.current?.click();
  };

  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允許重複選同一檔
    if (!file) return;

    // AC 1：上傳前即拒絕非 .xlsx / .xls 並顯示原因
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      alert('僅支援 Excel 檔（.xlsx / .xls），請重新選擇檔案');
      return;
    }

    // 匯入為「覆蓋更新」：既有資料會被檔案內容覆寫。
    // 需求 R60（PM 2026-07-04 拍板 Q1-Q4）：新增/覆蓋辨識鍵由
    // A 欄「產品貨編」改為 B 欄「系統辨識碼 systemCode」。此確認對話文字同步更新，
    // 避免操作者（沿用舊版說明）誤以為仍以產品貨編辨識（PM 2026-07-06 截圖詢問即此處）。
    // 一次性大量匯入屬不可逆的破壞性操作，上傳前先讓操作者確認檔名無誤。
    const confirmed = window.confirm(
      `即將以「${file.name}」匯入產品。\n\n` +
      '辨識依據為 B 欄「系統辨識碼」：B 欄有值 → 覆蓋既有產品；B 欄空白 → 新增為新產品；' +
      'B 欄有值但系統查無 → 該筆匯入失敗並列於失敗清單。\n' +
      '此操作會直接寫入資料庫，請確認檔案無誤後再繼續。'
    );
    if (!confirmed) return;

    setImporting(true);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/products/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || '匯入失敗');
        return;
      }
      setImportResult(data);
      fetchProducts();
    } catch (err) {
      alert(err.message || '匯入失敗');
    } finally {
      setImporting(false);
    }
  };

  // AC 10：失敗報表下載（CSV，含 UTF-8 BOM 供 Excel 正確顯示中文）
  const handleDownloadFailureReport = () => {
    const rows = importResult?.failures || [];
    if (rows.length === 0) return;
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Excel 列號', '產品貨編', '失敗原因'];
    const body = rows.map((r) => [r.rowNumber, r.productCode, r.reason].map(escape).join(','));
    const csv = '﻿' + [header.map(escape).join(','), ...body].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `產品匯入失敗報表_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // === Append panel search ===
  const handleAppendSearch = async () => {
    if (!appendKeyword.trim()) return;
    setAppendSearching(true);
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams({ keyword: appendKeyword, limit: 50 });
      const res = await fetch(`${API}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAppendResults(data.products || []);
      }
    } catch (err) {
      console.error('搜尋失敗', err);
    } finally {
      setAppendSearching(false);
    }
  };

  // 需求 R102 第八輪 Q8（PM 2026-08-03 留言「Q8 維持現行」）：
  // Q8 問的是「自動勾選要發生在哪一刻」——(現行) 按下面板下方的『確認追加』當下才併入 selectedIds，
  // 或改成按面板內每一列的『＋ 追加』就立刻反映到上方計數。PM 拍板維持現行，故本函式維持
  // 「只把商品放進 appendSelected 這個面板內的暫存清單」，不碰 selectedIds、不碰 products／total，
  // 上方「已選取 N 項產品」在這一步不會有任何變動（要按『確認追加』才會動，見 handleAppendConfirm）。
  // 因為維持兩段式，畫面上把這個時序講明：本列按鈕加 title、面板下方 hint 補「按『確認追加』後」，
  // 避免使用者按了『＋ 追加』看計數沒動、又回頭以為是 需求 R102 原本那個計數不連動的 bug。
  const handleAppendAdd = (product) => {
    if (appendSelected.find((p) => p._id === product._id)) return;
    setAppendSelected((prev) => [...prev, product]);
  };

  const handleAppendRemove = (productId) => {
    setAppendSelected((prev) => prev.filter((p) => p._id !== productId));
  };

  // 需求 R102（PM 2026-08-03 拍板）：修正「追加商品後上方『已選取 N 項產品』完全沒變動、
  // 表頭全選勾選框由已勾變未勾」。原因是本函式只做了 setProducts / setTotal，完全沒有動 selectedIds，
  // 追加進來的商品變成「在清單裡但沒被勾選」，計數自然停在原本的數字；而表頭勾選框判定是
  // 「本頁每一筆 _id 是否都在 selectedIds」（isCurrentPageAllSelected），多了未勾選的列就變 false。
  // Q1：原拍板 C（只有追加前已有勾選才自動勾），PM 2026-08-03 後續留言「按了＋追加就一律勾起來」
  // 已改判為 A —— 不看追加前有沒有勾選，一律把追加的商品併入 selectedIds（見下方 setSelectedIds）。
  // Q3=A：型錄管理頁（CatalogPage.handleAppendConfirm）一併修正，兩頁行為一致。
  // Q7（PM 2026-08-03 第七輪留言「不用固定顯示在每一頁」，推翻先前的 Q4=B）：追加的商品只加入
  // 「當下這一頁」的清單與總筆數，換頁／改篩選重新查詢後就不再出現；但它們的 _id 仍留在
  // selectedIds 裡，所以「已選取 N 項產品」的數量不會掉回去——這正是 PM 唯一要求要有的行為。
  const handleAppendConfirm = () => {
    // 已在目前清單裡的追加項不重複塞進清單、也不重複計入 total（AC2/AC4）
    const brandNew = appendSelected.filter((ap) => !products.find((p) => p._id === ap._id));
    setProducts((prev) => {
      const newProducts = brandNew.filter((ap) => !prev.find((p) => p._id === ap._id));
      return [...prev, ...newProducts];
    });
    setTotal((prev) => prev + brandNew.length);
    // Q1=A（PM 2026-08-03 留言「按了＋追加就一律勾起來」，推翻先前的 Q1=C）：
    // 不再判斷追加前 selectedIds 是不是空的，一律把追加的商品併入勾選。
    // － 追加前已有 50 項勾選 → 計數 50→52、表頭勾選框維持已勾（AC1/AC3）。
    // － 追加前 0 項勾選 → 追加 2 筆後計數 0→2（原 Q1=C 是維持 0，本次依 PM 留言改掉）。
    // Set 去重：重複追加同一筆不會多加（AC4）。
    // Q2（PM 詢問「已在清單裡但使用者原本刻意沒勾」的情況為何會發生）：追加面板的搜尋是全庫關鍵字搜尋
    // （handleAppendSearch 不帶目前列表的篩選條件），搜出來的結果本來就可能已經在目前清單裡；
    // 使用者若在列表上先手動取消勾該列、又在面板搜同一個關鍵字按「＋追加」，就會落到這個情況。
    // 此時一律以「使用者按了＋追加＝要納入操作範圍」為準：把它併回 selectedIds，該列的勾選框也
    // 同步變成已勾（勾選框 checked 直接讀 selectedIds），不會出現「計數加了但列沒打勾」的不一致（AC5）。
    setSelectedIds((prev) => Array.from(new Set([...prev, ...appendSelected.map((p) => p._id)])));
    setAppendPanelOpen(false);
    setAppendSelected([]);
    setAppendKeyword('');
    setAppendResults([]);
  };

  const totalPages = Math.ceil(total / limit) || 1;
  // 需求 R117 R3：顯示區間改由 <Pagination> 內部依 page/pageSize/total 計算，四頁同一條公式。

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // 需求 R17：PM 2026-05-07 補充指示——「更新日期」欄位語意為「資料最近一次被修改的時間」，
  // 而「新建立」也屬於最近一次的修改事件。因此這個欄位必須同時評估 createdAt（新建立）與
  // updatedAt（新修改），取兩者較晚者顯示。Mongoose timestamps:true 在新建文件時雖會把
  // updatedAt 設為與 createdAt 相同，但為了明確處理只有單邊時間戳的歷史資料（例如僅由 Excel
  // 匯入而從未經後台寫入的舊紀錄），仍以 max(createdAt, updatedAt) 為唯一顯示來源。
  const pickLatestModifiedAt = (p) => {
    const c = p?.createdAt ? new Date(p.createdAt).getTime() : 0;
    const u = p?.updatedAt ? new Date(p.updatedAt).getTime() : 0;
    const latest = Math.max(c, u);
    return latest > 0 ? new Date(latest) : null;
  };

  const canEdit = currentUser && hasPermission(currentUser.role, PERMISSIONS.PRODUCT_BASIC_EDIT);
  const isManager = currentUser && currentUser.role === 'manager';
  const isPurchasing = currentUser && (currentUser.role === 'purchasing' || currentUser.role === 'manager');

  if (!currentUser) return null;

  // Build 4 cost rows for a product (factory + 3 distributors)
  // 需求 R8：PM 2026-05-08 補充指示——徽章 label 維持「工廠／經銷N」固定字樣，
  // 詳情頁新增的 supplierName 改顯示在徽章右側（取代原本的 "—"），讓列表能對應到各成本來源的供應商名稱。
  // PM 同日後續追加要求：supplierName 文字字級需比表格預設（var(--font-size-sm)）小一級，
  // 故顯示時統一以 var(--font-size-xs) 呈現。
  const buildCostRows = (p) => {
    const rows = [];
    // Factory
    rows.push({
      label: '工廠',
      badgeClass: 'cost-factory',
      name: p.factoryCost?.supplierName || '—',
      unitPrice: p.factoryCost?.unitPrice,
      boxPrice: p.factoryCost?.boxPrice,
      moq: p.factoryCost?.moq || '—',
    });
    // Distributors
    for (let i = 0; i < 3; i++) {
      const d = p.distributorCosts?.[i];
      rows.push({
        label: `經銷${i + 1}`,
        badgeClass: `cost-dealer${i + 1}`,
        name: d?.supplierName || '—',
        unitPrice: d?.unitPrice,
        boxPrice: d?.boxPrice,
        moq: d?.moq || '—',
      });
    }
    return rows;
  };

  // Get catalog status badge
  const getCatalogBadge = (p) => {
    const badges = [];
    if (p.catalogStatus && catalogStatusBadge[p.catalogStatus]) {
      badges.push(catalogStatusBadge[p.catalogStatus]);
    }
    if (p.websiteStatus && websiteStatusBadge[p.websiteStatus]) {
      badges.push(websiteStatusBadge[p.websiteStatus]);
    }
    // 需求 R49 Phase 2：未處理改用新 .status-badge-mkt-unprocessed（紅），與型錄頁 .mkt-status-empty 同色票
    if (badges.length === 0) return [{ label: '未處理', cls: 'status-badge-mkt-unprocessed' }];
    return badges;
  };

  // Batch edit columns
  const batchEditColumns = [
    { key: 'productImage', label: '圖片', type: 'image' },
    { key: 'productName', label: '品名', type: 'text' },
    { key: 'englishName', label: '英文品名', type: 'text' },
    { key: 'spec', label: '規格', type: 'text' },
    { key: 'pcsPerBox', label: '箱入數', type: 'number' },
    { key: 'moq', label: 'MOQ', type: 'text' },
    { key: 'shelfLife', label: '保存期限', type: 'text' },
    { key: 'netWeight', label: '淨重(kg)', type: 'number' },
    { key: 'grossWeight', label: '毛重(kg)', type: 'number' },
    { key: 'barcode', label: '國際條碼', type: 'text' },
  ];

  /*
   * 需求 R117 R3：原本這裡有一份自寫的 renderPaginationButtons（maxVisible=5 的視窗、
   * 「上一頁／下一頁」文字鈕、額外釘一顆末頁），那正是 PM 指出「每頁排列都不同」的來源之一。
   * 已整份移除，改由 components/Common/Pagination.js 依客戶管理的排列統一渲染。
   */

  return (
    <div className="products-page">
      <Header
        title="📦 產品管理"
        user={currentUser}
        onLogout={handleLogout}
        onUserUpdate={setCurrentUser}
        actions={null}
      />

      <div className="container">
        {/* ========== 1. 頂部搜尋與多維度篩選區 ========== */}
        <section className="filter-section">
          <h2 className="filter-section-title">進階篩選</h2>

          {/* 層級篩選 + 關鍵字 */}
          <div className="filter-row">
            <div className="cascading-filters">
              {/* 需求 R106 R6（PM 2026-08-04 附圖：「產品管理列表除了加寬系列、類別
                  欄位，請將欄位呈現方式與型錄管理相同」）：R3 改多選時是就地沿用同頁標籤篩選的
                  .tag-dropdown（多欄 grid 的標籤選單，塞進 ~180px 的窄欄位會溢出，R5 才把欄數改成
                  自適應），呈現方式與型錄管理不一致。本輪改成與型錄管理同一組 .multi-select-*：
                  白底 trigger + 已選項目 chip + ▲/▼ 箭頭 + 單欄 checkbox 清單（樣式已抽到
                  styles/multiSelectDropdown.css，四頁共用單一來源）。
                  Q1=A：系列／類別／標籤三組之間是「任一符合」，故這裡挑幾個系列不會再縮小類別的結果。 */}
              <div className="filter-group" ref={seriesDropdownRef}>
                <label>系列 (可複選)</label>
                <div className="multi-select-dropdown">
                  <div
                    className="multi-select-trigger"
                    onClick={() => setSeriesDropdownOpen((prev) => !prev)}
                  >
                    <div className="selected-tags">
                      {pendingSeries.length === 0
                        ? <span className="placeholder-text">全部系列</span>
                        : pendingSeries.map((s) => (
                          <span key={s} className="selected-tag">
                            {s}
                            <button className="tag-remove-btn" onClick={(e) => { e.stopPropagation(); togglePendingSeries(s); }}>×</button>
                          </span>
                        ))
                      }
                    </div>
                    <span className="arrow">{seriesDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  {seriesDropdownOpen && (
                    <div className="multi-select-menu">
                      {seriesOptions.map((o) => (
                        <label key={o._id} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={pendingSeries.includes(o.value)}
                            onChange={() => togglePendingSeries(o.value)}
                          />
                          {o.value}
                        </label>
                      ))}
                      {seriesOptions.length === 0 && (
                        <div className="multi-select-empty">尚無系列</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <span className="cascading-arrow">→</span>
              <div className="filter-group" ref={categoryDropdownRef}>
                <label>類別 (可複選)</label>
                <div className="multi-select-dropdown">
                  <div
                    className="multi-select-trigger"
                    onClick={() => setCategoryDropdownOpen((prev) => !prev)}
                  >
                    <div className="selected-tags">
                      {pendingCategory.length === 0
                        ? <span className="placeholder-text">全部類別</span>
                        : pendingCategory.map((c) => (
                          <span key={c} className="selected-tag">
                            {c}
                            <button className="tag-remove-btn" onClick={(e) => { e.stopPropagation(); togglePendingCategory(c); }}>×</button>
                          </span>
                        ))
                      }
                    </div>
                    <span className="arrow">{categoryDropdownOpen ? '▲' : '▼'}</span>
                  </div>
                  {categoryDropdownOpen && (
                    <div className="multi-select-menu">
                      {categoryOptions.map((o) => (
                        <label key={o._id} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={pendingCategory.includes(o.value)}
                            onChange={() => togglePendingCategory(o.value)}
                          />
                          {o.value}
                        </label>
                      ))}
                      {categoryOptions.length === 0 && (
                        <div className="multi-select-empty">尚無類別</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 需求 R106 R5（PM 2026-08-04 回饋）：關鍵字欄過寬會把改成多選的
                系列／類別擠到換行，加 filter-group-keyword 讓 CSS 能單獨調整它的寬度權重。 */}
            <div className="filter-group filter-group-keyword">
              <label>關鍵字檢索</label>
              <input
                type="text"
                className="form-control"
                placeholder="輸入貨編／中英文名稱／供應商／條碼..."
                value={pendingKeyword}
                onChange={(e) => setPendingKeyword(e.target.value)}
              />
            </div>
          </div>

          {/* 標籤系統 */}
          <div className="filter-row">
            {/* 需求 R123 R3（PM 2026-08-31：「讓第二列一樣可以對齊，標籤(Tags)
                欄位的寬度至類別即可，建立時間區間則與關鍵字檢索對齊」）：本欄原本只有泛用的
                .filter-group（flex:1），會把整列剩餘寬度吃光、右緣落在關鍵字欄中間，與上一列對不齊。
                加 .products-filter-tags 讓 CSS 能單獨給它與上一列 .cascading-filters 相同的
                寬度權重（見 ProductsPage.css）。 */}
            <div className="filter-group products-filter-tags" ref={tagsDropdownRef}>
              <label>標籤 (Tags)</label>
              <div className={`tag-dropdown${tagsDropdownOpen ? ' open' : ''}`}>
                <div
                  className="tag-dropdown-trigger form-control"
                  onClick={() => setTagsDropdownOpen((prev) => !prev)}
                  style={{ flexWrap: 'wrap', gap: '4px', height: 'auto', minHeight: '36px' }}
                >
                  {pendingTags.length === 0 ? (
                    <span className="tag-dropdown-placeholder">請選擇標籤（可複選）</span>
                  ) : (
                    pendingTags.map((tag) => (
                      <span key={tag} className="tag" onClick={(e) => e.stopPropagation()}>
                        {tag}
                        <button className="tag-remove" onClick={(e) => { e.stopPropagation(); togglePendingTag(tag); }}>×</button>
                      </span>
                    ))
                  )}
                  <span className="tag-dropdown-arrow" style={{ marginLeft: 'auto' }}>▾</span>
                </div>
                <div className="tag-dropdown-menu">
                  {tagOptions.map((opt) => (
                    <label key={opt._id} className="tag-dropdown-item">
                      <input
                        type="checkbox"
                        checked={pendingTags.includes(opt.value)}
                        onChange={() => togglePendingTag(opt.value)}
                      />
                      {opt.value}
                    </label>
                  ))}
                  {tagOptions.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-sm)' }}>
                      尚無標籤
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 需求 R123 R2（PM 2026-08-25：「也套用在產品管理列表頁，位置亦同」）：
                位置＝標籤右側、「清除條件／確定篩選」按鈕左側，且與標籤同一個 .filter-row
                （本頁按鈕自成最後一列，故 DOM 上「按鈕左側」＝排在按鈕那列之前）。
                DOM／class 逐字沿用型錄新品目錄與報價單兩頁：.new-product-date-range /
                .catalog-filter-label「建立時間區間（最長 2 個月）」/ .date-range-inputs /
                兩個 .date-input / .date-range-sep「～」/ .date-range-error。
                窄視窗交給 .filter-row 現有的 flex-wrap（≤768px 該列本來就整排改直排）。 */}
            <div className="filter-group products-filter-date-range">
              <div className="new-product-date-range">
                <label className="catalog-filter-label">建立時間區間（最長 2 個月）</label>
                <div className="date-range-inputs">
                  <input
                    type="date"
                    className="date-input"
                    value={pendingCreatedFrom}
                    onChange={(e) => { setPendingCreatedFrom(e.target.value); setDateRangeError(''); }}
                  />
                  <span className="date-range-sep">～</span>
                  <input
                    type="date"
                    className="date-input"
                    value={pendingCreatedTo}
                    onChange={(e) => { setPendingCreatedTo(e.target.value); setDateRangeError(''); }}
                  />
                </div>
                {dateRangeError && <div className="date-range-error">{dateRangeError}</div>}
              </div>
            </div>
          </div>

          {/* 確定篩選 */}
          <div className="filter-row">
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 'var(--spacing-xs)', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                <button className="btn btn-outline btn-sm" onClick={handleClearFilters} style={{ whiteSpace: 'nowrap' }}>
                  清除條件
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleApplyFilters} style={{ whiteSpace: 'nowrap', padding: 'var(--spacing-xs) var(--spacing-lg)' }}>
                  確定篩選
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ========== 追加商品面板 (inline) ========== */}
        <div className={`append-panel${appendPanelOpen ? ' open' : ''}`}>
          <div className="append-panel-header">
            <div className="append-panel-title">追加商品至目前篩選清單</div>
            <button className="append-panel-close" onClick={() => setAppendPanelOpen(false)}>×</button>
          </div>

          <div className="append-search-row">
            <div ref={appendSearchRef} className={`append-search-wrap${appendResults.length > 0 ? ' has-results' : ''}`}>
              <input
                className="append-search-input"
                type="text"
                placeholder="輸入貨編／產品名稱／條碼搜尋..."
                value={appendKeyword}
                onChange={(e) => setAppendKeyword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAppendSearch(); }}
              />
              <div className="append-search-dropdown">
                {appendResults.map((item) => {
                  const alreadySelected = appendSelected.find((p) => p._id === item._id);
                  const alreadyInList = products.find((p) => p._id === item._id);
                  return (
                    <div key={item._id} className="append-search-item">
                      <span className="append-search-item-code">{item.productCode}</span>
                      <span className="append-search-item-name">{item.productName}</span>
                      {alreadyInList || alreadySelected ? (
                        <span className="append-search-item-add already">已選取</span>
                      ) : (
                        <button
                          className="append-search-item-add"
                          /* 需求 R102 第八輪 Q8=維持現行：按這裡只是先放進下方待追加清單，
                             上方「已選取 N 項產品」要等按「確認追加」才會變動。 */
                          title="先加入下方待追加清單；按「確認追加」後才會加入目前清單並自動勾選（已選取數量一併增加）"
                          onClick={() => handleAppendAdd(item)}
                        >＋ 追加</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={handleAppendSearch} disabled={appendSearching}>
              {appendSearching ? '搜尋中...' : '搜尋'}
            </button>
          </div>

          {appendSelected.length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
              {/* 需求 R102 第八輪 Q8=維持現行：這裡是「待追加」暫存清單，尚未計入上方選取計數。 */}
              已選取追加（{appendSelected.length} 筆，尚未計入上方選取計數）：
            </div>
          )}
          <div className="append-selected-area">
            {appendSelected.length === 0 ? (
              <span className="append-selected-empty">尚未選取任何商品</span>
            ) : (
              appendSelected.map((item) => (
                <span key={item._id} className="append-selected-chip">
                  {item.productCode} {item.productName}
                  <button className="append-selected-chip-remove" onClick={() => handleAppendRemove(item._id)}>×</button>
                </span>
              ))
            )}
          </div>

          <div className="append-panel-footer">
            {/* 需求 R102 第七輪：原文寫「以藍色標示區別」但實際上沒有這個標示（第二輪加的黃色「追加」
                標記亦已依 PM 指示移除），改成如實描述現在的行為。
                第八輪 Q8（PM「Q8 維持現行」）：補上時序「按下『確認追加』後」，明講自動勾選發生在
                確認的那一刻，而不是在面板內按每一列的「＋ 追加」時。 */}
            <span className="append-hint">按下「確認追加」後，追加的商品會加入目前清單並自動勾選（已選取數量一併增加）；換頁或改篩選後不再顯示於清單</span>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setAppendPanelOpen(false); setAppendSelected([]); setAppendResults([]); setAppendKeyword(''); }}>取消</button>
              <button
                className="btn btn-sm"
                style={{ backgroundColor: '#1976d2', color: 'white', borderColor: '#1565c0' }}
                onClick={handleAppendConfirm}
                disabled={appendSelected.length === 0}
              >
                確認追加 {appendSelected.length} 筆
              </button>
            </div>
          </div>
        </div>

        {/* ========== 2. 操作功能列 ========== */}
        {/* 需求 R66: class 名稱從 action-bar 改為 products-action-bar 以避開與 QuotesPage.css
            同名 class 全域衝突（詳見 ProductsPage.css 註解）。 */}
        <section className="products-action-bar">
          {/* 需求 R62（PM 2026-07-14 R10 留言）：「將批量操作與管理功能識別為兩個不同
              區塊，維持管理功能最左邊與『選取：』左緣切齊，批量操作最右邊與『共 N 筆產品』右緣切齊」。
              作法＝把 action-bar 由「單列 flex-wrap」改為「兩列堆疊」（每列 .action-bar-row 各自
              justify-content: space-between）：
                第一列＝選取工具（左） ｜ 共 N 筆產品（右）
                第二列＝管理功能（左） ｜ 批量操作（右）
              兩列共用相同左右內緣，因此管理功能左緣＝選取左緣、批量操作右緣＝共 N 筆右緣，
              且兩者被明確拆成兩個獨立區塊（不再包在同一 .action-bar-right wrapper 內）。 */}
          {/* 第一列：選取工具（左） ｜ 共 N 筆產品（右） */}
          <div className="action-bar-row">
          {/* 需求 R62（PM 2026-07-10 拍板方案 A）：保留表頭「全選目前頁面」勾選框（AC1），
              此處新增「全選所有商品（total）」跨分頁按鈕、顯示選取計數讓範圍一目了然。 */}
          <div className="action-group">
            <span className="action-group-label">選取：</span>
            <button
              type="button"
              className="btn btn-outline btn-sm products-select-all-across"
              onClick={handleSelectAllAcrossPages}
              disabled={selectAllAcrossLoading || total === 0 || batchEditMode}
              title="勾選目前篩選結果中的所有商品（包含其他分頁）"
            >
              {selectAllAcrossLoading
                ? '處理中...'
                : (total > 0 && selectedIds.length >= total ? '取消全部勾選' : `全選所有商品（${total}）`)}
            </button>
            {/* 需求 R102 第二輪：換頁後被勾選的商品多半在別的分頁上，畫面看不到勾但計數照算，
                這裡直接把「有幾項不在目前頁面」寫出來，消除「數字對不上畫面」的誤導。
                第七輪（PM 2026-08-03 留言「不用固定顯示在每一頁，同時將『追加』兩字取消」）：
                移除「追加 N 筆固定顯示於每一頁」這一段與其 tooltip——固定顯示行為本身已拿掉，
                追加的商品換頁後就跟其他分頁上被勾選的商品一樣，統一由 offPageSelectedCount 交代。 */}
            <span
              className="products-selection-count"
              title={
                offPageSelectedCount > 0
                  ? `目前頁面上有 ${selectedIds.length - offPageSelectedCount} 項被勾選，另有 ${offPageSelectedCount} 項在其他分頁（換頁不會取消勾選）`
                  : undefined
              }
            >
              已選取 <strong>{selectedIds.length}</strong> 項產品（共 {total} 項）
              {offPageSelectedCount > 0 && (
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  ｜其中 {offPageSelectedCount} 項不在目前頁面
                </span>
              )}
            </span>
          </div>

          {/* 共 N 筆產品：第一列最右端（被 space-between 推到右內緣）。 */}
          <div className="result-count">
            共 <strong>{total.toLocaleString()}</strong> 筆產品
          </div>
          </div>

          {/* 第二列：管理功能（左，與第一列「選取：」左緣切齊） ｜ 批量操作（右，與第一列「共 N 筆產品」右緣切齊）。
              兩者是兩個獨立區塊，靠 .action-bar-row 的 space-between 分別頂到左右內緣。 */}
          <div className="action-bar-row">
          <div className="action-group action-group-manage">
            <span className="action-group-label">管理功能：</span>
            {canEdit && (
              <button className="btn btn-success btn-sm" onClick={() => navigate('/products/new')}>新增產品</button>
            )}
            {isManager && (
              <button
                className="btn btn-sm"
                style={{ backgroundColor: 'var(--color-danger)', color: 'var(--color-text-inverse)', border: '1px solid var(--color-danger-dark)' }}
                onClick={handleBatchDelete}
                disabled={!selectedIds.length}
              >
                刪除產品
              </button>
            )}
            <button
              className={`delisted-filter-btn${pendingDelisted ? ' active' : ''}`}
              onClick={handleDelistedToggle}
              aria-pressed={pendingDelisted}
            >
              {pendingDelisted ? '✓ 下架篩選中' : '下架商品篩選器'}
            </button>
            <button
              className="btn btn-sm btn-append-trigger"
              onClick={() => setAppendPanelOpen((prev) => !prev)}
            >
              ＋ 追加商品
            </button>
          </div>

          {/* 需求 R62（PM 2026-07-14 R10）：批量操作為第二列右側獨立區塊，右緣對齊第一列「共 N 筆產品」。 */}
          <div className="action-group action-group-batch">
            <span className="action-group-label">批量操作：</span>
            {isPurchasing && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleImportFileSelected}
                />
                <button className="btn btn-outline btn-sm" onClick={handleImportProducts} disabled={importing}>
                  {importing ? '匯入中…' : '匯入產品'}
                </button>
              </>
            )}
            {isPurchasing && (
              <button
                className="btn btn-sm"
                style={{ backgroundColor: 'var(--color-info)', color: '#fff', border: '1px solid var(--color-info)' }}
                onClick={handleEnterBatchEdit}
                disabled={batchEditMode}
              >
                批量修改
              </button>
            )}
            {/* 需求 R108 Q4：匯出中禁用按鈕並改字（防連點），不顯示筆數提示 */}
            <button
              className="btn btn-outline btn-sm"
              onClick={handleExportCostTable}
              disabled={exportingCostTable}
            >
              {exportingCostTable ? '匯出中…' : '匯出成本表'}
            </button>
          </div>
          </div>
        </section>

        {/* Batch edit save/cancel bar */}
        {batchEditMode && (
          <section className="batch-edit-bar">
            <div className="batch-edit-bar-info">
              正在編輯 <strong>{Object.keys(batchEditData).length}</strong> 筆產品
            </div>
            <div className="batch-edit-bar-actions">
              <button className="btn btn-sm btn-batch-save" onClick={handleBatchEditSave} disabled={batchSaving}>
                {batchSaving ? '儲存中...' : '儲存修改'}
              </button>
              <button className="btn btn-sm btn-batch-cancel" onClick={handleBatchEditCancel} disabled={batchSaving}>
                取消
              </button>
            </div>
          </section>
        )}

        {/* Hidden file input for batch image upload */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleBatchImageFileChange}
        />

        {/* ========== 3. 產品清單表格 ==========
            需求 R138：PM 2026-08-24 拍板「評估不做，此案先暫停」。
            本表格維持「內層捲動框（max-height:70vh）＋ thead sticky 相對該容器」的現況，
            切勿改成整頁捲動、也不要為表頭另外加 --products-header-height 之類的視窗吸附偏移；
            阻礙與五道未拍板問題寫在 ProductsPage.css 的「3. 產品清單表格」段落，
            反向鎖見 tests/productsTableScroll.fixture.js。 */}
        <section className="products-table-container">
          <table className="products-data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="select-checkbox"
                    title="全選目前分頁"
                    checked={isCurrentPageAllSelected}
                    onChange={toggleSelectAll}
                    disabled={batchEditMode}
                  />
                </th>
                {batchEditMode ? (
                  <>
                    <th>產品編號</th>
                    {batchEditColumns.map((col) => {
                      const minWidth = col.type === 'image' ? 64 : col.type === 'number' ? 80 : 120;
                      return (
                        <th key={col.key} style={{ minWidth }}>{col.label}</th>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {/* 需求 R1：列表非批量編輯模式新增「圖片」欄。PM 2026-05-07 留言要求
                        「點擊縮圖-不動作」，故縮圖只做純展示，不掛任何 onClick / 連結 / hover 編輯遮罩。
                        欲更換圖片仍透過批量修改或詳情/編輯頁進行。 */}
                    <th style={{ width: 60 }}>圖片</th>
                    <th className="sortable" style={{ minWidth: 200 }}>貨編 / 名稱</th>
                    {/* 需求 R61（PM 2026-07-09 拍板「執行方案 A」）：
                        規格／箱入數欄加寬（長規格文字），以 .col-spec 設 min-width。 */}
                    <th className="sortable col-spec">
                      <div>規格</div>
                      <div>箱入數</div>
                    </th>
                    {/* 需求 R61 方案 A：淨重／毛重／材積為短數值欄，縮減寬度讓給規格與成本來源。 */}
                    <th className="sortable col-weight">
                      <div>淨重</div>
                      <div>毛重</div>
                      <div>材積</div>
                    </th>
                    {/* 需求 R61（PM 2026-07-09 同則留言追加）：成本來源欄加寬（供應商名稱過窄）。 */}
                    <th className="col-cost-source">成本來源</th>
                    <th>
                      <div>標準成本</div>
                      <div>(TWD單價/箱價)</div>
                    </th>
                    <th>MOQ</th>
                    <th>
                      <div>保存期限</div>
                      <div>國際條碼</div>
                      {/* 需求 R17：經 PM 2026-05-07 確認，欄位語意為「資料最近一次被修改的時間」，
                          一律以 Mongoose 自動 timestamp 為準，不再 fallback 顯示手動字串 lastUpdatedDate。
                          PM 同日補充：「新建立」也屬於最近一次的修改事件，故顯示時取
                          max(createdAt, updatedAt)，新建立的商品也會在此欄位顯示日期。
                          DB 上的 lastUpdatedDate 欄位保留供 Excel 匯入紀錄與後續其他用途，但不再於此列表顯示。*/}
                      <div title="此處顯示「資料最近一次被修改的時間」，同時涵蓋「新建立」（createdAt）與「新修改」（updatedAt），取兩者較晚者；任一後台寫入：新增、編輯、批量修改、圖片上傳皆會自動更新。">更新日期</div>
                    </th>
                    <th style={{ width: 70 }}>狀態</th>
                    <th style={{ width: 70 }}>操作</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const isEditing = batchEditMode && batchEditData[p._id];
                const costRows = buildCostRows(p);
                const isDelisted = p.productStatus === 'delisted';

                return (
                  <tr key={p._id} className={isEditing ? 'batch-edit-row' : ''} style={isDelisted && !batchEditMode ? { opacity: 0.6 } : undefined}>
                    <td className="col-checkbox">
                      <input
                        type="checkbox"
                        className="select-checkbox"
                        checked={selectedIds.includes(p._id)}
                        onChange={() => toggleSelect(p._id)}
                        disabled={batchEditMode}
                      />
                    </td>
                    {batchEditMode ? (
                      <>
                        <td>
                          {/* 需求 R102 第七輪（PM 2026-08-03「將『追加』兩字取消」）：移除本格的追加標記。 */}
                          <span className="product-code">{p.productCode}</span>
                        </td>
                        {isEditing ? (
                          batchEditColumns.map((col) => {
                            if (col.type === 'image') {
                              const imgUrl = batchEditData[p._id]?.[col.key] || '';
                              return (
                                <td key={col.key}>
                                  <div
                                    className="product-thumb-wrapper product-thumb-editable product-image-download-host"
                                    onClick={() => triggerBatchImageUpload(p._id)}
                                    title="點擊更新產品圖片"
                                  >
                                    {imgUrl ? (
                                      <img className="product-thumb" src={imgUrl} alt={p.productName} />
                                    ) : (
                                      <span className="no-image-badge">無圖片</span>
                                    )}
                                    <span className="product-thumb-edit-overlay">更換圖片</span>
                                    {/* 需求 R88（PM 2026-07-30 追加留言）：把截圖上傳也補到
                                        「產品管理 批量修改」圖片框。compact 版按鈕自帶 stopPropagation，不會
                                        觸發外層縮圖既有的檔案上傳；回填走 handleBatchEditChange 寫入 batchEditData；
                                        不支援 getDisplayMedia 的瀏覽器 ScreenshotCapture 回傳 null，不渲染。 */}
                                    <span
                                      className="product-thumb-screenshot-btn"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ScreenshotCapture
                                        compact
                                        onUploaded={(url) => handleBatchEditChange(p._id, 'productImage', url || '')}
                                      />
                                    </span>
                                    {/* 需求 R152（PM 2026-09-01）：滑鼠移到縮圖時，右下角
                                        出現下載小圖示（截圖鈕在左下角，左右對稱）。批量修改中的圖片框
                                        顯示的是 batchEditData 的**未存檔**圖片，所以下載的來源也用 imgUrl，
                                        使用者剛截圖／剛換上的那張就能直接存回電腦。
                                        元件自帶 stopPropagation，不會觸發外層的「點擊更新產品圖片」上傳。 */}
                                    <ProductImageDownloadButton src={imgUrl} productName={p.productName} />
                                  </div>
                                </td>
                              );
                            }
                            return (
                              <td key={col.key}>
                                <input
                                  type={col.type}
                                  className="form-control form-control-sm batch-edit-input"
                                  value={batchEditData[p._id]?.[col.key] ?? ''}
                                  onChange={(e) => handleBatchEditChange(p._id, col.key, e.target.value)}
                                  step={col.type === 'number' ? 'any' : undefined}
                                />
                              </td>
                            );
                          })
                        ) : (
                          <>
                            <td>
                              {/* 需求 R152：批量修改模式下「未勾選那幾列」的縮圖也是產品圖片，
                                  一樣要能 hover 下載（PM 只講「產品管理的產品縮圖」，沒有排除某些列）。
                                  這裡原本是裸 <img>，沒有定位容器，故補一層 .product-image-download-wrap；
                                  該 class 是 line-height:0 的 inline-block，不改變縮圖既有的 48×48 外觀。 */}
                              {p.productImage ? (
                                <span className="product-image-download-wrap product-image-download-host">
                                  <img className="product-thumb" src={p.productImage} alt={p.productName} />
                                  <ProductImageDownloadButton src={p.productImage} productName={p.productName} />
                                </span>
                              ) : (
                                <span className="no-image-badge">無圖片</span>
                              )}
                            </td>
                            <td>{p.productName}</td>
                            <td>{p.englishName || '—'}</td>
                            <td>{p.spec || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{p.pcsPerBox ?? '—'}</td>
                            <td>{p.factoryCost?.moq || '—'}</td>
                            <td>{p.shelfLife || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{p.netWeight ?? '—'}</td>
                            <td style={{ textAlign: 'center' }}>{p.grossWeight ?? '—'}</td>
                            <td>{p.barcode || '—'}</td>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* 圖片：純展示，PM 2026-05-07 指定「點擊縮圖-不動作」，
                            因此不掛 onClick / Link / hover overlay；要換圖請走批量修改或詳情/編輯頁。 */}
                        <td style={{ width: 60 }}>
                          {/* 需求 R152（PM 2026-09-01）：產品管理列表的縮圖 hover 下載。
                              「點擊縮圖-不動作」的舊決議（PM 2026-05-07）維持不變 —— 外層仍然沒有 onClick，
                              只有右下角這顆按鈕可點，而它自帶 stopPropagation／preventDefault。 */}
                          {p.productImage ? (
                            <span className="product-image-download-wrap product-image-download-host">
                              <img
                                className="product-thumb"
                                src={p.productImage}
                                alt={p.productName}
                                loading="lazy"
                              />
                              <ProductImageDownloadButton src={p.productImage} productName={p.productName} />
                            </span>
                          ) : (
                            <span className="no-image-badge">無圖片</span>
                          )}
                        </td>
                        {/* 貨編 / 名稱 */}
                        <td>
                          <div className="product-info">
                            {/* 需求 R102 第七輪（PM 2026-08-03「將『追加』兩字取消」）：移除追加標記；
                                固定顯示於每一頁的行為同步拿掉，標記本來就只是為了解釋那個行為而存在。 */}
                            <span className="product-code">{p.productCode}</span>
                            <span className="product-name">{p.productName}</span>
                            {p.englishName && <span className="product-name-en">{p.englishName}</span>}
                          </div>
                        </td>
                        {/* 規格 / 箱入數（需求 R61 方案 A：.col-spec 加寬） */}
                        <td className="col-spec">
                          <div>{p.spec || '—'}</div>
                          <div>{p.pcsPerBox ?? '—'}</div>
                        </td>
                        {/* 淨重 / 毛重 / 材積（需求 R61 方案 A：.col-weight 縮減） */}
                        <td className="col-weight">
                          <div>{p.netWeight != null ? p.netWeight : '—'}</div>
                          <div>{p.grossWeight != null ? p.grossWeight : '—'}</div>
                          <div>
                            {/* 需求 R6（PM 2026-05-08 二次確認）：
                                預估來源時，僅徽章本身顯示黃色底色（透過 .cbm-estimate），
                                材積數值「不」加底色，所以這裡不再用 .cbm-estimate-highlight 包外層。 */}
                            {p.volumeCuft != null ? parseFloat(p.volumeCuft).toFixed(2) : '—'}
                            {p.volumeMethod && (
                              <span className={`cbm-badge ${volumeMethodClass(p.volumeMethod)}`}>
                                {volumeMethodLabel(p.volumeMethod)}
                              </span>
                            )}
                          </div>
                        </td>
                        {/* 成本來源（需求 R61 方案 A 追加：.col-cost-source 加寬） */}
                        <td className="col-cost-source">
                          {costRows.map((row, i) => (
                            <div key={i}>
                              <span className={`cost-source-badge ${row.badgeClass}`}>{row.label}</span>
                              <span style={{ fontSize: 'var(--font-size-xs)' }}>{row.name}</span>
                            </div>
                          ))}
                        </td>
                        {/* 標準成本 (TWD單價/箱價) */}
                        <td className="price-cell">
                          {costRows.map((row, i) => (
                            <div key={i}>
                              {row.unitPrice != null || row.boxPrice != null
                                ? `${fmtPrice(row.unitPrice)} \u2002/\u2002 ${fmtPrice(row.boxPrice)}`
                                : '—'}
                            </div>
                          ))}
                        </td>
                        {/* MOQ */}
                        <td>
                          {costRows.map((row, i) => (
                            <div key={i}>{row.moq || '—'}</div>
                          ))}
                        </td>
                        {/* 保存期限 / 條碼 / 更新日期 */}
                        <td>
                          <div>{p.shelfLife || '—'}</div>
                          {/* 需求 R19：條碼字型統一改為與「標準成本」(.price-cell) 相同的 'Noto Sans TC', sans-serif，
                              避免欄位間 monospace / 黑體字體風格不一致。font-size 維持 var(--font-size-xs)。 */}
                          <div style={{ fontFamily: "'Noto Sans TC', sans-serif", fontSize: 'var(--font-size-xs)' }}>{p.barcode || '—'}</div>
                          {/* 需求 R17：PM 確認此欄位顯示「資料最近一次被修改的時間」。
                              PM 2026-05-07 補充：「新建立」也視為最近一次的修改事件，
                              因此取 max(createdAt, updatedAt) 顯示，避免新建立但尚未編輯的商品
                              此欄位顯示為空白。*/}
                          {(() => {
                            const latest = pickLatestModifiedAt(p);
                            const isCreatedOnly = latest && p.createdAt && p.updatedAt
                              && new Date(p.createdAt).getTime() === new Date(p.updatedAt).getTime();
                            const tip = latest
                              ? `${isCreatedOnly ? '新建立日期' : '資料最近一次被修改的時間'}：${latest.toLocaleString('zh-TW')}`
                              : '尚無更新紀錄';
                            return (
                              <div
                                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
                                title={tip}
                              >{formatDate(latest)}</div>
                            );
                          })()}
                        </td>
                        {/* 狀態 */}
                        <td className="col-status">
                          {getCatalogBadge(p).map((b, i) => (
                            <span key={i} className={`status-badge ${b.cls}`} style={{ display: 'block', marginBottom: i < getCatalogBadge(p).length - 1 ? 2 : 0 }}>
                              {b.label}
                            </span>
                          ))}
                        </td>
                        {/* 操作：詳情/編輯 使用 <Link> anchor，左鍵維持同分頁 SPA 切換，
                            右鍵可叫出瀏覽器原生「在新分頁開啟連結」（需求 R30 決議，
                            取代原 需求 R2 採用的 navigate() button；勿退回 <button> 或改成 window.open） */}
                        <td className="col-actions">
                          <div className="row-actions">
                            <Link
                              className="row-action-btn btn-detail"
                              to={`/products/${p._id}`}
                              onClick={persistListStateForReturn}
                            >
                              詳情
                            </Link>
                            {canEdit && (
                              <Link
                                className="row-action-btn btn-edit"
                                to={`/products/${p._id}?mode=edit`}
                                onClick={persistListStateForReturn}
                              >
                                編輯
                              </Link>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {products.length === 0 && (
                <tr>
                  <td colSpan={batchEditMode ? 2 + batchEditColumns.length : 11} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>
                    尚無產品資料
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/*
            分頁控制
            需求 R117 R3（PM 2026-08-18）：排列對齊客戶管理——每頁顯示（左）／頁碼＋跳頁框（中）／
            筆數資訊（右）。本頁原本是「筆數資訊 → 每頁顯示 → 頁碼」而且頁碼是文字版
            「上一頁／下一頁」，與客戶管理的 ‹／› 不同，現在統一由共用元件渲染。
            跳頁只改 page，selectedIds 跨頁勾選邏輯一行未動（AC9）。
          */}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            pageSizeOptions={[25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
          />
        </section>
      </div>

      {/* Product Modal */}
      <ProductModal
        open={modalOpen}
        mode={modalMode}
        product={selectedProduct}
        currentUser={currentUser}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
        onModeChange={setModalMode}
      />

      {/* 需求 R39：匯入結果報告（AC 10） */}
      {importResult && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setImportResult(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 24, width: 'min(640px, 92vw)', maxHeight: '82vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>產品匯入結果</h3>
            <p style={{ margin: '8px 0' }}>
              總筆數 <strong>{importResult.total}</strong>　·
              成功 <strong style={{ color: 'var(--color-success, #16a34a)' }}>{importResult.success}</strong>
              （新增 {importResult.inserted}／更新 {importResult.updated}）　·
              失敗 <strong style={{ color: 'var(--color-danger, #dc2626)' }}>{importResult.failed}</strong>
            </p>

            {importResult.failed > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0 6px' }}>
                  <span>失敗列清單：</span>
                  <button className="btn btn-outline btn-sm" onClick={handleDownloadFailureReport}>下載失敗報表 (CSV)</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '4px 6px' }}>列號</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '4px 6px' }}>貨編</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '4px 6px' }}>原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.failures.map((f, i) => (
                      <tr key={i}>
                        <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 6px' }}>{f.rowNumber}</td>
                        <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 6px' }}>{f.productCode || '—'}</td>
                        <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 6px' }}>{f.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button className="btn btn-success btn-sm" onClick={() => setImportResult(null)}>關閉</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
